/**
 * Application Constants
 * 
 * Centralized configuration for values that were previously hardcoded
 * across multiple components. Default values here match the DB seed
 * in CompanySettings. Settings from server override these at runtime.
 * 
 * USAGE: Import { CONSTANT } from '../config/appConstants';
 * For settings-aware values, use useSettings() hook instead.
 */

// ── Airport Detection (defaults, overridden by settings.airportDetectionRules) ──
export const DEFAULT_AIRPORT_RULES = [
    { keywords: ['jfk', 'john f. kennedy', 'kennedy airport'], zip: '11430' },
    { keywords: ['lga', 'laguardia'], zip: '11371' },
    { keywords: ['ewr', 'newark airport', 'newark liberty'], zip: '07114' },
];

/**
 * Check if an address is an airport based on configurable rules.
 * @param {string} address - The address to check
 * @param {Array} rules - Airport detection rules (from settings or DEFAULT_AIRPORT_RULES)
 * @returns {boolean}
 */
export const isAirport = (address, rules = DEFAULT_AIRPORT_RULES) => {
    if (!address) return false;
    const lower = address.toLowerCase();
    return rules.some(rule =>
        rule.keywords.some(kw => lower.includes(kw)) ||
        (rule.zip && lower.includes(rule.zip))
    );
};

/**
 * Parse airport detection rules from settings JSON string.
 * @param {object} settings - CompanySettings object
 * @returns {Array} parsed rules or defaults
 */
export const getAirportRules = (settings) => {
    if (!settings?.airportDetectionRules) return DEFAULT_AIRPORT_RULES;
    try { return JSON.parse(settings.airportDetectionRules); }
    catch { return DEFAULT_AIRPORT_RULES; }
};

// ── Pricing Defaults (fallbacks; runtime values come from settings) ──
export const PRICING_DEFAULTS = {
    rushHourDriverCompRatio: 0.7,       // Driver gets 70% of rush hour surcharge
    minimumFare: 70,
    ccFeeRate: 0.04,                    // 4% CC processing fee
};

// ── Payment Type Labels (dispatcher view) ──
export const PAYMENT_TYPE_LABELS = {
    cash: 'Cash',
    zelle: 'Zelle',
    driverCC: 'Driver Collects CC',
    dispatcherCC: 'Card on File',
    prepaid: 'Prepaid',
    other: 'Other',
};

// ── Pagination ──
export const DEFAULT_PAGE_SIZE = 50;

// ── Days of Week ──
export const DAYS_OF_WEEK = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
];

export const DAYS_OF_WEEK_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const DAYS_OF_WEEK_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// ── Phone Number Formatting ──
/**
 * Format a string of digits as a US phone number: (XXX) XXX-XXXX
 * @param {string} digits - raw digits only
 * @returns {string} formatted phone string
 */
export const formatPhoneNumber = (digits) => {
    if (!digits) return '';
    const cleaned = digits.replace(/\D/g, '');
    if (cleaned.length <= 3) return `(${cleaned}`;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

// ── Ride Status Colors & Labels ──
export const RIDE_STATUS_COLORS = {
    canceled: 'error',
    open: 'primary',
    assigned: 'warning',
    arriving: 'warning',
    scheduled: 'warning',
    atPickup: 'info',
    inProgress: 'default',
    paymentPending: 'secondary',
    droppedOff: 'secondary',
    completed: 'success',
};

export const RIDE_STATUS_LABELS = {
    canceled: 'Canceled',
    open: 'Open',
    assigned: 'Assigned',
    arriving: 'Arriving',
    atPickup: 'At Pickup',
    inProgress: 'In Progress',
    paymentPending: 'Payment Pending',
    droppedOff: 'Dropped Off',
    completed: 'Completed',
    scheduled: 'Scheduled',
};

export const RIDE_STATUS_STYLE_OVERRIDES = {
    inProgress: { backgroundColor: '#FDD835', color: '#000', fontWeight: 'bold' },
    Driving: { backgroundColor: '#FDD835', color: '#000', fontWeight: 'bold' },
};

export const DRIVER_STATUS_COLORS = {
    'No Driver Found': 'error',
    'Inactive': 'error',
    'Available': 'success',
    'En-Route': 'warning',
    'Driving': 'default',
};

// ── Map Marker Colors (DriverTracking) ──
export const MAP_MARKER_COLORS = {
    driver: {
        driving: { fill: '#b10606', stroke: '#ffffff' },
        drivingOffline: { fill: '#FFD700', stroke: '#ffffff' },
        available: { fill: '#0a9702', stroke: '#ffffff' },
        upcomingRide: { fill: '#FF9800', stroke: '#FF5722' },
        highlighted: { fill: '#00BCD4', stroke: '#E91E63' },
    },
    ride: {
        missed: { fill: '#f44336', stroke: '#b71c1c' },
        inProgress: { fill: '#ffeb3b', stroke: '#f57f17' },
        future: { fill: '#4caf50', stroke: '#1b5e20' },
        completed: { fill: '#9e9e9e', stroke: '#616161' },
        atPickup: { fill: '#2196f3', stroke: '#0d47a1' },
        droppedOff: { fill: '#9c27b0', stroke: '#4a148c' },
    },
};

// ── Map Ride Status Colors & Labels (DriverTracking view — hex values for map markers/chips) ──
export const MAP_RIDE_STATUS_COLORS = {
    missed: '#f44336',
    atPickup: '#2196f3',
    inProgress: '#ffeb3b',
    droppedOff: '#9c27b0',
    completed: '#9e9e9e',
    future: '#4caf50',
};

export const MAP_RIDE_STATUS_LABELS = {
    missed: 'Missed/Late',
    atPickup: 'At Pickup',
    inProgress: 'In Progress',
    droppedOff: 'Dropped Off',
    completed: 'Completed',
    future: 'Upcoming',
};

// ── Dashboard Metric Card Colors ──
export const DASHBOARD_CARD_COLORS = {
    recurringThisWeek: '#0288d1',    // info blue
    todaysRecurring: '#0097a7',      // teal (today's recurring)
    todaysRides: '#2e7d32',          // success green
    futureRides: '#607d8b',          // blue-grey
    openRides: '#ed6c02',            // warning orange
    assignedRides: '#1976d2',        // primary blue
    ridesInProgress: '#d32f2f',      // error red
    completedRides: '#388e3c',       // lighter success green
    activeDrivers: '#9c27b0',        // secondary purple
    driversOnJob: '#7b1fa2',         // deeper purple
    unsettledDrivers: '#ff9800',     // orange
    offlineDrivers: '#757575',       // grey
};

// ── Sound Service Defaults ──
// These Web Audio API fallback tones mirror the character of the WAV files.
export const SOUND_DEFAULTS = {
    types: {
        message:   { frequency: 880,  duration: 0.45, urgent: false }, // bell ping
        urgent:    { frequency: 1100, duration: 0.40, urgent: true  }, // triple beep (fallback plays one pulse)
        sent:      { frequency: 700,  duration: 0.28, urgent: false }, // whoosh sweep
        success:   { frequency: 100,  duration: 0.60, urgent: false }, // low rumble (car driving off)
        broadcast: { frequency: 600,  duration: 0.42, urgent: false }, // two-tone chime
    },
    defaultVolume: 0.5,
    gain: { urgent: 0.4, normal: 0.3 },
    urgentKeywords: ['cancel', 'reassign', 'remove', 'urgent', 'emergency', 'help'],
};

// ── Locale / Formatting Defaults ──
export const LOCALE_DEFAULTS = {
    locale: 'en-US',
    use24HourTime: false,
};

/**
 * Get locale from settings, falling back to default.
 */
export const getLocale = (settings) => settings?.locale || LOCALE_DEFAULTS.locale;

/**
 * Get hour12 flag (opposite of use24HourTime setting).
 */
export const getHour12 = (settings) =>
    settings?.use24HourTime != null ? !settings.use24HourTime : !LOCALE_DEFAULTS.use24HourTime;

// ── Settings-aware getters ──────────────────────────────────────────
// Each getter reads the corresponding JSON setting and falls back to the static
// default above. Call from React components: `const colors = getSettingsDashboardCardColors(settings);`

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/** Recursively merges `override` onto `base`, so any keys missing from
 *  `override` fall back to the value from `base`. */
const deepMerge = (base, override) => {
    if (!isPlainObject(base)) return override;
    if (!isPlainObject(override)) return base;
    const out = { ...base };
    for (const key of Object.keys(override)) {
        out[key] = deepMerge(base[key], override[key]);
    }
    return out;
};

const safeParse = (jsonStr, fallback) => {
    if (!jsonStr) return fallback;
    try {
        const parsed = JSON.parse(jsonStr);
        // Merge over defaults so partial saved settings still get full defaults
        return deepMerge(fallback, parsed);
    } catch { return fallback; }
};

/** Dashboard card colors from settings */
export const getSettingsDashboardCardColors = (settings) =>
    safeParse(settings?.dashboardCardColors, DASHBOARD_CARD_COLORS);

/** Map marker fill/stroke colors from settings */
export const getSettingsMapMarkerColors = (settings) =>
    safeParse(settings?.mapMarkerColors, MAP_MARKER_COLORS);

/** Ride status → MUI palette name from settings */
export const getSettingsRideStatusColors = (settings) =>
    safeParse(settings?.rideStatusColors, RIDE_STATUS_COLORS);

/** Driver status → MUI palette name from settings */
export const getSettingsDriverStatusColors = (settings) =>
    safeParse(settings?.driverStatusColors, DRIVER_STATUS_COLORS);

/** Sound type configuration from settings */
export const getSettingsSoundSettings = (settings) =>
    safeParse(settings?.soundSettings, SOUND_DEFAULTS);

/** Map ride status hex colors (derived from map marker colors) */
export const getSettingsMapRideStatusColors = (settings) => {
    const markers = getSettingsMapMarkerColors(settings);
    const ride = markers?.ride || {};
    return {
        missed: ride.missed?.fill || MAP_RIDE_STATUS_COLORS.missed,
        atPickup: ride.atPickup?.fill || MAP_RIDE_STATUS_COLORS.atPickup,
        inProgress: ride.inProgress?.fill || MAP_RIDE_STATUS_COLORS.inProgress,
        droppedOff: ride.droppedOff?.fill || MAP_RIDE_STATUS_COLORS.droppedOff,
        completed: ride.completed?.fill || MAP_RIDE_STATUS_COLORS.completed,
        future: ride.future?.fill || MAP_RIDE_STATUS_COLORS.future,
    };
};

/** Page size from settings */
export const getSettingsPageSize = (settings) =>
    settings?.defaultPageSize || DEFAULT_PAGE_SIZE;

/** Sound enabled from settings */
export const getSettingsSoundEnabled = (settings) =>
    settings?.soundEnabled ?? true;

/** Sound volume from settings */
export const getSettingsSoundVolume = (settings) =>
    settings?.soundVolume ?? SOUND_DEFAULTS.defaultVolume;
